/* Global auto text colour: black or white depending on how dark the pixels
   directly behind the text are.

   Usage: put data-auto-contrast="<url of a small version of the image>" on the
   element that holds the text. Liquid side: img_url: '400x'.

   The element's own text box is measured and mapped back into image pixels
   through the object-fit: cover crop of its container, so only what is really
   behind the glyphs gets sampled.

   ponytail: samples a 400px thumbnail, not the displayed image. Keeps the big
   image free of crossorigin (no double fetch) and works before lazysizes
   swaps data-src in. */
(function () {
  var SRGB = new Float32Array(256);
  for (var i = 0; i < 256; i++) {
    var c = i / 255;
    SRGB[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }

  /* mean relative luminance (WCAG) of an RGBA pixel buffer */
  function luminance(data) {
    var sum = 0;
    for (var i = 0; i < data.length; i += 4) {
      sum += 0.2126 * SRGB[data[i]] + 0.7152 * SRGB[data[i + 1]] + 0.0722 * SRGB[data[i + 2]];
    }
    return sum / (data.length / 4);
  }

  /* whichever of white/black has the higher contrast ratio against L.
     Tips over at L = 0.179, the standard break-even point. */
  function pickColor(L) {
    var onWhite = (L + 0.05) / 0.05;
    var onBlack = 1.05 / (L + 0.05);
    return onBlack >= onWhite ? '#fff' : '#000';
  }

  /* box of the actual glyphs, not of the full-width element around them */
  function textRect(el) {
    try {
      var range = document.createRange();
      range.selectNodeContents(el);
      var r = range.getBoundingClientRect();
      if (r.width && r.height) return r;
    } catch (e) {
      /* fall through */
    }
    var r2 = el.getBoundingClientRect();
    return r2.width && r2.height ? r2 : null;
  }

  /* text box -> [x, y, w, h] in image pixels, assuming the image covers the
     positioning parent (object-fit: cover, centered). null when unmeasurable. */
  function region(el, imgW, imgH) {
    var parent = el.offsetParent || el.parentElement;
    if (!parent) return null;
    var box = parent.getBoundingClientRect();
    var text = textRect(el);
    if (!text || !box.width || !box.height) return null;

    var scale = Math.max(box.width / imgW, box.height / imgH);
    var offsetX = (box.width - imgW * scale) / 2;
    var offsetY = (box.height - imgH * scale) / 2;

    var x = (text.left - box.left - offsetX) / scale;
    var y = (text.top - box.top - offsetY) / scale;
    var w = text.width / scale;
    var h = text.height / scale;

    /* clip to the image, keep at least one pixel */
    var x0 = Math.max(0, Math.min(imgW - 1, Math.floor(x)));
    var y0 = Math.max(0, Math.min(imgH - 1, Math.floor(y)));
    var x1 = Math.max(x0 + 1, Math.min(imgW, Math.ceil(x + w)));
    var y1 = Math.max(y0 + 1, Math.min(imgH, Math.ceil(y + h)));
    return [x0, y0, x1 - x0, y1 - y0];
  }

  var cache = new Map();

  /* resolves with a 2d context holding the thumbnail, plus its size */
  function sample(url) {
    if (cache.has(url)) return cache.get(url);
    var p = new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        var canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 32;
        canvas.height = img.naturalHeight || 32;
        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        resolve({ ctx: ctx, width: canvas.width, height: canvas.height });
      };
      img.onerror = reject;
      img.src = url;
    });
    cache.set(url, p);
    return p;
  }

  function apply(root) {
    var nodes = (root || document).querySelectorAll('[data-auto-contrast]:not([data-auto-contrast-done])');
    for (var i = 0; i < nodes.length; i++) {
      (function (el) {
        var url = el.getAttribute('data-auto-contrast');
        el.setAttribute('data-auto-contrast-done', '');
        if (!url) return;
        sample(url).then(function (thumb) {
          var box = region(el, thumb.width, thumb.height) || [0, 0, thumb.width, thumb.height];
          var data = thumb.ctx.getImageData(box[0], box[1], box[2], box[3]).data;
          el.style.color = pickColor(luminance(data));
        }).catch(function () {
          /* keep whatever colour the section setting rendered */
        });
      })(nodes[i]);
    }
  }

  window.applyAutoContrast = apply;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { apply(); });
  } else {
    apply();
  }

  /* self-check: open any page with ?ac-test in the URL */
  if (location.search.indexOf('ac-test') !== -1) {
    var px = function (r, g, b, n) {
      var a = new Uint8ClampedArray(n * 4);
      for (var i = 0; i < a.length; i += 4) { a[i] = r; a[i + 1] = g; a[i + 2] = b; a[i + 3] = 255; }
      return a;
    };
    console.assert(luminance(px(0, 0, 0, 4)) === 0, 'black = 0');
    console.assert(Math.abs(luminance(px(255, 255, 255, 4)) - 1) < 1e-6, 'white = 1');
    console.assert(pickColor(luminance(px(0, 0, 0, 4))) === '#fff', 'dark image -> white text');
    console.assert(pickColor(luminance(px(255, 255, 255, 4))) === '#000', 'light image -> black text');
    console.assert(pickColor(0.178) === '#fff' && pickColor(0.18) === '#000', 'break-even at 0.179');

    /* region(): a text box in the top left maps to the top left of the image */
    var fake = {
      offsetParent: { getBoundingClientRect: function () { return { left: 0, top: 0, width: 200, height: 200 }; } },
      getBoundingClientRect: function () { return { left: 10, top: 10, width: 40, height: 8 }; }
    };
    var r = region(fake, 100, 100); /* square image in a square box -> scale 2 */
    console.assert(r && r[0] === 5 && r[1] === 5 && r[2] === 20 && r[3] === 4, 'region maps text box into image px, got ' + r);

    /* wide image in a square box: cover crops the sides, x shifts right */
    var r2 = region(fake, 200, 100); /* scale 2, rendered 400x200, offsetX -100 */
    console.assert(r2 && r2[0] === 55 && r2[1] === 5, 'region accounts for the cover crop, got ' + r2);
    console.log('auto-contrast self-check done');
  }
})();
